import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, downloadCsv, formatError } from "@/lib/api";
import { PageTitle } from "./AdminApp";

const COLS = [
  ["planned_households", "Planned households"],
  ["planned_people", "Planned people"],
  ["checked_in_households", "Checked-in households"],
  ["checked_in_people", "Checked-in people"],
  ["absent_households", "Marked absent"],
  ["walk_in_households", "Checked-in, not planned"],
];

export default function Attendance() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/attendance").then((r) => setRows(r.data)).catch((e) => toast.error(formatError(e)));
  }, []);
  const exportCsv = () =>
    downloadCsv("/admin/attendance/export", {}, `attendance-${new Date().toISOString().slice(0, 10)}.csv`)
      .then(() => toast.success("CSV downloaded"))
      .catch((e) => toast.error(formatError(e)));

  return (
    <div data-testid="admin-attendance">
      <PageTitle title="Attendance report">
        <button onClick={exportCsv} className="flex h-10 items-center gap-2 rounded-full border border-amber-500/50 px-4 text-sm font-semibold text-amber-400 transition-colors hover:bg-amber-500 hover:text-neutral-950" data-testid="export-attendance-csv">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </PageTitle>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {rows.map((r) => {
          const pct = r.planned_households ? Math.round((r.checked_in_households / r.planned_households) * 100) : 0;
          return (
            <div key={r.day} className="rounded-lg border border-neutral-800 bg-neutral-900 p-5" data-testid={`attendance-day-${r.day}`}>
              <p className="font-display text-xl text-gold">{r.label}</p>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-neutral-500">Planned</p>
                  <p className="font-display text-3xl text-neutral-100" data-testid={`attendance-planned-${r.day}`}>{r.planned_households}<span className="ml-1 text-base text-neutral-500">/ {r.planned_people} ppl</span></p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-widest text-emerald-500">Checked in</p>
                  <p className="font-display text-3xl text-emerald-300" data-testid={`attendance-checked-${r.day}`}>{r.checked_in_households}<span className="ml-1 text-base text-neutral-500">/ {r.checked_in_people} ppl</span></p>
                </div>
              </div>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-neutral-800">
                <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-8 overflow-x-auto rounded-lg border border-neutral-800">
        <table className="w-full min-w-[640px] text-left text-sm" data-testid="attendance-table">
          <thead className="bg-neutral-900 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-3 py-3">Day</th>
              {COLS.map(([k, l]) => <th key={k} className="px-3 py-3">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.day} className="border-t border-neutral-900">
                <td className="px-3 py-3 font-display text-neutral-100">{r.label}</td>
                {COLS.map(([k]) => <td key={k} className="px-3 py-3 text-neutral-300">{r[k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
